import { graphql } from '../../gql';
import { useApiMutation } from '../../query/hooks';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { ModalActions } from '../../modal/components/ModalActions';
import { ModalContent } from '../../modal/components/ModalContent';
import { useModalContext } from '../../modal/ModalContext';
import { z } from 'zod';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useSnackbar } from '../../store/snackbar';

const updatePersonaMutation = graphql(`
	mutation removePersonaAvatar_updatePersona($personaId: UUID!) {
		updatePersona(by: { id: $personaId }, data: { avatar: { disconnect: true } }) {
			ok
			errorMessage
		}
	}
`);

export const removePersonaAvatarModalSchema = z.object({
	personaId: z.string(),
});

export const RemovePersonaAvatarModal: FC<z.infer<typeof removePersonaAvatarModalSchema>> = ({ personaId }) => {
	const intl = useIntl();
	const { openSnackbar } = useSnackbar();

	const { mutate: removeAvatar, isPending: isRemoveAvatarPending } = useApiMutation(updatePersonaMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: result.updatePersona.ok,
				toastMessages: {
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.updatePersona.errorMessage,
				openSnackbar,
			});
		},
	});
	const { closeModal } = useModalContext();

	return (
		<>
			<ModalContent
				isDelete
				title={intl.formatMessage({ id: 'modal.remove-persona-avatar.title' })}
				description={intl.formatMessage({ id: 'modal.remove-persona-avatar.description' })}
			/>
			<ModalActions
				isDelete
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={() => {
					removeAvatar(
						{ personaId },
						{
							onSettled: () => {
								closeModal();
							},
						},
					);
				}}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={
					isRemoveAvatarPending ? intl.formatMessage({ id: 'common.deleting' }) : intl.formatMessage({ id: 'common.delete' })
				}
				disablePrimaryButton={isRemoveAvatarPending}
			/>
		</>
	);
};
